import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { getConfig } from './config';

export class NewPasswordRequiredError extends Error {
  readonly cognitoUser: CognitoUser;

  constructor(cognitoUser: CognitoUser) {
    super('NEW_PASSWORD_REQUIRED');
    this.name = 'NewPasswordRequiredError';
    this.cognitoUser = cognitoUser;
  }
}

function pool(): CognitoUserPool | null {
  const cfg = getConfig();
  if (!cfg.cognitoUserPoolId || !cfg.cognitoClientId) return null;
  return new CognitoUserPool({
    UserPoolId: cfg.cognitoUserPoolId,
    ClientId: cfg.cognitoClientId,
  });
}

export function getCurrentUser(): CognitoUser | null {
  return pool()?.getCurrentUser() ?? null;
}

export function getSession(): Promise<CognitoUserSession | null> {
  const user = getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) resolve(null);
      else resolve(session);
    });
  });
}

export async function getIdToken(): Promise<string | null> {
  const session = await getSession();
  return session?.getIdToken().getJwtToken() ?? null;
}

export function signIn(email: string, password: string): Promise<CognitoUserSession> {
  const p = pool();
  if (!p) {
    localStorage.setItem('supporto_dev_user', email);
    return Promise.resolve({} as CognitoUserSession);
  }
  const user = new CognitoUser({ Username: email, Pool: p });
  const auth = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(auth, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => reject(new NewPasswordRequiredError(user)),
    });
  });
}

export function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

export function signOut(): void {
  localStorage.removeItem('supporto_dev_user');
  getCurrentUser()?.signOut();
}

export async function isAuthenticated(): Promise<boolean> {
  if (localStorage.getItem('supporto_dev_user')) return true;
  const session = await getSession();
  return !!session;
}
